import type { SelectMenuItem, NavigationMenuItem } from '@nuxt/ui'
import type { AppComponentItem } from '~/types/common/item'
import type { OfficeDetails } from '~/pages/about/contact/hook/type'
import { configIcon } from '~/config/icon'

export const useConfigMenu = () => {
  const { t } = useI18n()
  const route = useRoute()
  // 避免语言代码丢失的路由跳转方式
  const localePath = useLocalePath()

  /** 主菜单 */
  const appMenu = computed<NavigationMenuItem[]>(() => [
    // 首页
    {
      label: t('page.index.title'),
      to: localePath('/'),
    },
    // 订单
    {
      label: t('page.order.title'),
      children: [
        {
          label: t('page.order.create.title'),
          icon: 'mdi:truck-outline',
          description: t('page.order.create.subTitle'),
          active: route.path.endsWith('/order/create'),
          to: localePath('/order/create'),
        },
        {
          label: t('page.order.quote.title'),
          icon: 'lsicon:order-outline',
          description: t('page.order.quote.subTitle'),
          active: route.path.endsWith('/order/quote'),
          to: localePath('/order/quote'),
        },
      ],
    },
    // 跟踪
    {
      label: t('page.track.title'),
      children: [
        {
          label: t('page.track.order.title'),
          icon: 'mdi:magnify',
          description: t('page.track.order.subTitle'),
          active: route.path.endsWith('/track/order'),
          to: localePath('/track/order'),
        },
      ],
    },
    // 业务
    {
      label: t('page.business.title'),
      children: [
        {
          label: t('page.business.transport.title'),
          icon: 'mdi:truck-outline',
          description: t('page.business.transport.subTitle'),
          active: route.path.endsWith('/business/transport'),
          to: localePath('/business/transport'),
        },
        {
          label: t('page.business.storage.title'),
          icon: 'vaadin:storage',
          description: t('page.business.storage.subTitle'),
          active: route.path.endsWith('/business/storage'),
          to: localePath('/business/storage'),
        },
        {
          label: t('page.business.cold-chain.title'),
          icon: 'mdi:snowflake',
          description: t('page.business.cold-chain.subTitle'),
          active: route.path.endsWith('/business/cold-chain'),
          to: localePath('/business/cold-chain'),
        },
        {
          label: t('page.business.customs.title'),
          icon: 'mdi:briefcase-outline',
          description: t('page.business.customs.subTitle'),
          active: route.path.endsWith('/business/customs'),
          to: localePath('/business/customs'),
        },
      ],
    },
    // 关于
    {
      label: t('page.about.title'),
      children: [
        {
          label: t('page.about.brand.title'),
          icon: 'material-symbols:book-ribbon-outline',
          description: t('page.about.brand.subTitle'),
          active: route.path.endsWith('/about/brand'),
          to: localePath('/about/brand'),
        },
        {
          label: t('page.about.news.title'),
          icon: 'ri:newspaper-line',
          description: t('page.about.news.subTitle'),
          active: route.path.endsWith('/about/news'),
          to: localePath('/about/news'),
        },
        {
          label: t('page.about.course.title'),
          icon: 'mdi:hourglass',
          description: t('page.about.course.subTitle'),
          active: route.path.endsWith('/about/course'),
          to: localePath('/about/course'),
        },
        {
          label: t('page.about.contact.title'),
          icon: 'mingcute:phone-line',
          description: t('page.about.contact.subTitle'),
          active: route.path.endsWith('/about/contact'),
          to: localePath('/about/contact'),
        },
      ],
    },
  ])

  /** 隐私页面列表 */
  const policyMenu: AppComponentItem[] = [
    {
      label: 'page.policy.user-agreement.title',
      value: '/policy/user-agreement',
    },
    {
      label: 'page.policy.privacy-policy.title',
      value: '/policy/privacy-policy',
    },
  ]

  /** 配送国家或区域下拉菜单 */
  const countrySelect = [
    {
      label: t('location.china.title'),
      value: 'cn',
      icon: configIcon.china,
    },
    {
      label: t('location.china_hk.title'),
      value: 'cn_hk',
      icon: configIcon.china_hk,
    },
    {
      label: t('location.vietnam.title'),
      value: 'vn',
      icon: configIcon.vietnam,
    },
    {
      label: t('location.thailand.title'),
      value: 'th',
      icon: configIcon.thailand,
    },
    {
      label: t('location.laos.title'),
      value: 'la',
      icon: configIcon.laos,
    },
    {
      label: t('location.singapore.title'),
      value: 'sg',
      icon: configIcon.singapore,
    },
    {
      label: t('location.malaysia.title'),
      value: 'my',
      icon: configIcon.malaysia,
    },
  ] satisfies SelectMenuItem[]

  /** 电话区号选择菜单 */
  const areaCodeSelect = [
    {
      label: '+86',
      icon: configIcon.china,
    },
    {
      label: '+852',
      icon: configIcon.china_hk,
    },
    {
      label: '+84',
      icon: configIcon.vietnam,
    },
    {
      label: '+66',
      icon: configIcon.thailand,
    },
    {
      label: '+856',
      icon: configIcon.laos,
    },
    {
      label: '+65',
      icon: configIcon.singapore,
    },
    {
      label: '+60',
      icon: configIcon.malaysia,
    },
    {
      label: '+1',
      icon: configIcon.usa,
    },
  ] satisfies SelectMenuItem[]

  /** 要咨询的业务类型下拉菜单 */
  const consultedBusinessSelect = [
    {
      label: t('select.consultedBusiness.transport'),
      value: 'transport',
    },
    {
      label: t('select.consultedBusiness.warehouse'),
      value: 'warehouse',
    },
    {
      label: t('select.consultedBusiness.customs'),
      value: 'customs',
    },
    {
      label: t('select.consultedBusiness.cold-chain'),
      value: 'cold-chain',
    },
  ] satisfies SelectMenuItem[]

  /** 交易频率下拉菜单 */
  const transFrequencySelect = [
    {
      label: t('select.transFrequency.daily', { frequency: '<=100' }),
      value: 'daily<=100',
    },
    {
      label: t('select.transFrequency.daily', { frequency: '>100' }),
      value: 'daily>100',
    },
    {
      label: t('select.transFrequency.weekly', { frequency: '<=100' }),
      value: 'weekly<=100',
    },
    {
      label: t('select.transFrequency.weekly', { frequency: '>100' }),
      value: 'weekly>100',
    },
    {
      label: t('select.transFrequency.monthly', { frequency: '<=100' }),
      value: 'monthly<=100',
    },
    {
      label: t('select.transFrequency.monthly', { frequency: '>100' }),
      value: 'monthly>100',
    },
  ] satisfies SelectMenuItem[]

  /** 装运方式 */
  const shipmentTypeSelect = [
    {
      label: t('select.shipmentType.ftl.title'),
      description: t('select.shipmentType.ftl.desc'),
      icon: 'lucide:container',
      image: '/img/order/create/ftl.webp',
      value: 'ftl',
    },
    {
      label: t('select.shipmentType.ltl.title'),
      description: t('select.shipmentType.ltl.desc'),
      icon: 'i-lucide-box',
      image: '/img/order/create/ltl.webp',
      value: 'ltl',
    },
  ] satisfies SelectMenuItem[]

  /** 办公室位置以及联络信息 */
  const officeDetails: OfficeDetails[] = [
    {
      name: 'page.about.contact.office.hongkong',
      address:
        'Flat 11, 27/F., Tower 2 Ever Gain Plaza, 88 Container Port Road, Kwai Chung, N.T. 117, Hong Kong',
      telephone: '+852-3618-9668',
      flagIcon: configIcon.china_hk,
      region: 'china_hk',
      imgUrl: '/img/map/hongkong.webp',
      mapPlatform: {
        gaode: 'https://surl.amap.com/q4v7Aq59b2',
        baidu: 'https://j.map.baidu.com/08/X3Yi',
        google: 'https://maps.app.goo.gl/HYKt1J4XGMMaGEr8A',
      },
    },
    {
      name: 'page.about.contact.office.shenzhen',
      address:
        'Unit 1201&1202, Building 1, Dongle Building 2019 Shennan Dong Road, Luo Hu District, Shenzhen 518001, China.',
      warehouse:
        'Building 1, No. 288, Nancheng Section, Shuilian Landscape, Nancheng Street, Dongguan City, Guangdong Province',
      flagIcon: configIcon.china,
      region: 'china',
      telephone: '+86-755-8832-3730',
      fax: '+86-755-8832-3731',
      imgUrl: '/img/map/shenzhen.webp',
      mapPlatform: {
        gaode: 'https://surl.amap.com/iIcPjzDzeBg',
        baidu: 'https://j.map.baidu.com/58/pQz',
        tencent:
          'https://mmapgwh.map.qq.com/shortlink/short?l=_286e727a1b18f38297558ab9037f1613&tempSource=pcMap',
        google: 'https://maps.app.goo.gl/qsp8e9nmhoQJ6tSYA',
      },
    },
    {
      name: 'page.about.contact.office.pingxiang',
      address:
        'Room 101, First Floor, Warehouse Management Center, Guangxi Pingxiang Comprehensive Bonded Zone, Chongzuo City, Guangxi Province',
      flagIcon: configIcon.china,
      region: 'china',
      telephone: '+86-771-855-8083',
      fax: '+86-771-855-8077',
      warehouse: 'A3-1#A3-2# Warehouse, Comprehensive Bonded Zone, Pingxiang, Guangxi, China No.',
      imgUrl: '/img/map/pingxiang.webp',
      mapPlatform: {
        gaode: 'https://surl.amap.com/q9h2zqxr64K',
        baidu: 'https://j.map.baidu.com/08/VZwc',
        tencent: 'https://j.map.baidu.com/08/VZwc',
        google: 'https://maps.app.goo.gl/b2WeBFxWWPBjUMdFA',
      },
    },
    {
      name: 'page.about.contact.office.langson',
      address: 'Ben xe xuan cuong khu cua khau huu nghi huyen cao loc tinh lang son viet nam',
      flagIcon: configIcon.vietnam,
      region: 'vietnam',
      telephone: '+84-25-385-5566',
      imgUrl: '/img/map/langson.webp',
      mapPlatform: {
        google: 'https://maps.app.goo.gl/2bRDocoBniqZh65z6',
      },
    },
    {
      name: 'page.about.contact.office.hanoi',
      address:
        'Crystal Network Logistics (Vietnam) Co., Ltd. Room 401, 4th Floor, Sannam Building Duy Tan Str., Cau Giay District, Hanoi, Vietnam',
      flagIcon: configIcon.vietnam,
      region: 'vietnam',
      warehouse: 'ICD Warehouse - TS 9, Tien Son Industrial Park, Tien Son, BacNinh, Vietnam',
      telephone: '+84-24-3795-5740',
      fax: '+84-24-3795-5743',
      imgUrl: '/img/map/hanoi.webp',
      mapPlatform: {
        google: 'https://maps.app.goo.gl/vJbjef1aFAVjHzoz7',
      },
    },
    {
      name: 'page.about.contact.office.hochiminh',
      address:
        'Room A1.06.14, the Goldview building, 346 Ben Van Don Street, Ward 1 ,District 4 , Ho Chi Minh City , Vietnam',
      flagIcon: configIcon.vietnam,
      region: 'vietnam',
      warehouse: 'ICD Warehouse - TS 9, Tien Son Industrial Park, Tien Son, BacNinh, Vietnam',
      telephone: '+84-28-5445-6859',
      fax: '+84-28-5445-6858',
      imgUrl: '/img/map/hochiminh.webp',
      mapPlatform: {
        google: 'https://maps.app.goo.gl/QazigZ3jaJ9FU2zj9',
      },
    },
    {
      name: 'page.about.contact.office.bangkok',
      address: '8/8 Happy Place Road, Khlong Sam Prawet, Latkrabang, Bangkok 10520, Thailand',
      flagIcon: configIcon.thailand,
      region: 'thailand',
      warehouse:
        '299/1, Ladkrabang Industrial Estate, Chalongkrung Road, Lamplatiew, Latkrabang, Bangkok 10520, Thailand',
      telephone: '+66-2-828-8881',
      imgUrl: '/img/map/bangkok.webp',
      mapPlatform: {
        google: 'https://maps.app.goo.gl/reyzutQVFRDumXxU8',
      },
    },
    {
      name: 'page.about.contact.office.mukdahan',
      address: '313/15 Friendship Bridge Road,  Bang Sai Yai, Mueang Mukdahan Mukdahan, Thailand',
      flagIcon: configIcon.thailand,
      region: 'thailand',
      telephone: '+66-83-417-3844',
      imgUrl: '/img/map/mukdahan.webp',
      mapPlatform: {
        google: 'https://maps.app.goo.gl/RM69jh47FYrpzPFTA',
      },
    },
  ]

  return {
    appMenu,
    policyMenu,
    countrySelect,
    shipmentTypeSelect,
    areaCodeSelect,
    consultedBusinessSelect,
    transFrequencySelect,
    officeDetails,
  }
}
